
import { Component, Inject, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import NavHeader from "@/components/NavHeader.vue";
import {
	ViewMode,
	ViewObject,
	ViewPageDimensions,
	ViewCellDTO,
	ViewCellObject,
	ViewReadDTO,
	ViewPage,
	PageKeeperResponse
} from "@/store/views/types.ts";
import ViewShare from "@/components/views/ViewShare.vue";
import { last } from "lodash";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import Sessions from "@/components/Sessions.vue";
import { views } from "../../store/views";
import { BFormInput } from "bootstrap-vue";
import viewsApi from "../../services/api.views.service";
import api from "@/services/api.service";
import ViewsEventBus from "@/services/views/viewsEventBus";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { Dropdown, DropdownItem } from "@sureview/vuejs-sv-dropdown";
import { cloneDeep } from "lodash";
import vSelect from "vue-select"
import { UserGroup } from "@/store/user-management/types"
import router from '@/router';
import { PrefTypeRequest, PrefTypesEnum } from "@/store/settings/types";

const ViewState = namespace("views");
const SettingsStore = namespace("settings");

@Component({
	components: {
		"nav-header": NavHeader,
		session: Sessions,
		"view-share": ViewShare,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"sv-dropdown": Dropdown,
		"sv-dropdown-item": DropdownItem,
		"v-select": vSelect
	},
	mixins: [validationMixin],
	validations: {
		title: {
			required,
			minLength: minLength(1)
		}
	}
})
export default class ViewHeader extends Vue {
	public $refs!: {
		viewTitleInput: BFormInput;
		viewsScrollbar: any;
	};

	@Prop({ type: Boolean, default: false }) private mobile: boolean;
	@Prop({ type: Boolean, default: true }) private isMainViewWindow: boolean;
	@Prop({ type: Number, default: 0}) private viewIDOverride: number;
	@Prop({ type: Number, default: 0}) private viewPageNumber: number;
	@Prop({ type: String, default: ""}) private viewGUID: string;

	@ViewState.Action loadUserViews: any;
	@ViewState.Action loadAdhocUserView: any;
	@ViewState.Action loadViews: any;
	@ViewState.Action loadViewUpdateTime: any;
	@ViewState.Action loadViewContentUpdateTime: any;
	@ViewState.Action("saveView") save: any;
	@ViewState.Action("addUserView") add: any;
	@ViewState.Action("deleteView") delete: any;
	@ViewState.Action("removeUserView") remove: any;
	@ViewState.Action getViewDetails: any;
	@ViewState.Action getViewProperties: any;
	@ViewState.Action loadUpdatedCellContent: any;
	@ViewState.Action loadPluginTypes: any;
	@ViewState.Action toggleLock: any;

	@ViewState.Mutation private setSelectedView: (view: ViewObject) => void;
	@ViewState.Mutation private setSelectedViewPageNumber: (pageNumber: number) => void;
	@ViewState.Mutation deletePageCells: (pageNumber: number) => void;
	@ViewState.Mutation setSelectedViewTitle: (title: string) => void;
	@ViewState.Mutation setSelectedViewCells: (cells: ViewCellObject[]) => void;
	@ViewState.Mutation private setViewDimensions: (dimensions: ViewPageDimensions) => void;
	@ViewState.Mutation setViewMode: any;
	@ViewState.Mutation setSelectedTileType: any;
	@ViewState.Mutation setSideBarExtended: any;
	@ViewState.Mutation setSelectedViewPublic: any;
	@ViewState.Mutation setSelectedViewHasUserGroups: (hasUserGroups: boolean) => void;
	@ViewState.Mutation updateViewPages: (pages: ViewPage[]) => void;
	@ViewState.Mutation updateSelectedViewPages: (pages: ViewPage[]) => void;

	@ViewState.Getter("getViews") views: any;
	@ViewState.Getter("getUserViews") userViews: ViewObject[];
	@ViewState.Getter("getSelectedView") selectedView: ViewObject;
	@ViewState.Getter("getPluginTypes") tileTypes: any;
	@ViewState.Getter getSelectedTileType: any;
	@ViewState.Getter getSideBarExtended: boolean;
	@ViewState.Getter getSideBarPinned: boolean;
	@ViewState.Getter getViewMode: ViewMode;

	@Getter("getUserId") public currentUserId!: number;
	@Getter getUserGroupId: number;
	@Getter getPermissions: any;
	@Getter isFullUser;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@SettingsStore.Action getPrefsFromList: (prefEnumList: PrefTypeRequest[]) => void;
	@SettingsStore.Getter getPrefRawValue: (prefType: PrefTypeRequest) => string;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	public viewFilter: string = null;
	public viewCellNextId: number = 20;
	private resizeHoverWidth: number = 0;
	private resizeHoverHeight: number = 0;
	private maxWidth: number = 5;
	private maxHeight: number = 5;
	private newView: ViewObject = null;
	private titleError: boolean = false;
	private isDeleteConfirmationShown: boolean = false;
	private isDeletePageConfirmationShown: boolean = false;
	private checkChangesInterval = null;
	private layoutExpired: boolean = false;
	private showTooltip: boolean = true;
	private isAdding: boolean = false;
	private errorMessage: string = "";
	private isShareModalOpen: boolean = false;
	private viewToShare: any = {};
	private showMaxPluginError: boolean = false;
	private contentLastUpdated: number = null;
	public originalView: ViewReadDTO = null;
	public modalShow = false;
	public viewToLock: ViewObject = null;	

	private userGroups: UserGroup[] = [];
	private viewUserGroups: UserGroup[] = [];
	private viewUserGroupAdded: boolean = false;

	private pageKeeperInterval: NodeJS.Timeout;
	private pageKeeperExpectedResponses: PageKeeperResponse[] = [];
	private pageKeeperResponses: PageKeeperResponse[] = [];
	private requestHasBeenRespondedTo: boolean = false;
	private pagesExemptFromPageKeeperChecks: number[] = [];
	private pagesOpenedRecently: number[] = [];

	// unique value for gridlayout to pickup on when update ...
	// because of watcher detecting change to selected tile type
	// causing the grid layout to remove items unpredicably with
	// multi window views when tileType is set to null
	private noTileTypeId : number = -997853215;

	public get navTitle(): string {
		return this.isMainViewWindow ? 'Views' : `${this.selectedView?.title ?? 'View'}, page : ${this.viewPageNumber}`;
	}

	private defaultGridSizePrefRequest: PrefTypeRequest = {
		prefTypeID: PrefTypesEnum.ViewsDefaultGridSize,
		groupID: null
	}

	public get defaultViewGridSize(): string {	
		let tempValue = this.getPrefRawValue(this.defaultGridSizePrefRequest);	
		if(!tempValue || tempValue.length < 3 || tempValue === "No Pref Found" || !tempValue.includes('x')) {
			return "5x5";
		}	
		return tempValue;
	}

	//Feature Flags
	public get isViewUserGroupsEnabled() {
		return this.getFeature(["Views", "UserGroups"]);
	}

	public get shouldUseDedicatedCloseButton() {
		return this.getFeature(["Views", "UseDedicatedCloseButton"]);
	}

	public get isMultiWindowViewEnabled() {
		return this.mobile ? false : this.getFeature(["Views", "MultiWindowView"]);
	}

	public get isPageKeeperEnabled() {
		return this.mobile ? false : this.getFeature(["Views", "PageKeeper"]);
	}

	private get currentUserViews(): ViewObject[] {
		if (this.newView) {
			return this.userViews.concat([this.newView]);
		}

		return this.userViews;
	}

	public get availableViews() {
		if (this.viewFilter) {
			return this.views.filter(view => view.title.toLowerCase().includes(this.viewFilter));
		}

		return this.views;
	}

	private get userHasEditPermission() {
		if (
			this.selectedView &&
			// user can edit their own private view
			((this.selectedView.ownerUserId == this.currentUserId) ||
				// user can edit restricted view if they have inherited group permission
				(this.selectedView.public && this.selectedView.hasUserGroups) || 
					// user can edit public view if they have 'canEditPublicView' permission or admin
					(this.selectedView.public && (this.getPermissions.canEditPublicView || this.getPermissions.isAdmin)))
		) {
			return true;
		}

		return false;
	}
	private get userHasClonePermission() {
		return this.getPermissions.canCloneViews;
	}

	private get userHasLockedPermission(){
		return this.getPermissions.canLockViews;
	}

	public get editing() {
		return this.getViewMode === ViewMode.Edit;
	}

	public get sideBarExtended() {
		return this.getSideBarExtended;
	}

	public set sideBarExtended(state: boolean) {
		this.setSideBarExtended(state);
	}

	public get title() {
		if (!this.selectedView) {
			return "";
		}

		return this.selectedView.title;
	}

	public set title(value: string) {
		this.setSelectedViewTitle(value);
	}

	public get width(): number {
		if (!this.selectedView || !this.selectedView.pages) {
			return 0;
		}

		let pages = this.selectedView.pages;
		let pageNumberIndex = this.selectedView.pages.findIndex(page => page.pageNumber === this.viewPageNumber);

		return this.isMainViewWindow ? pages[0].columns : pages[pageNumberIndex].columns;
	}

	public get height(): number {
		if (!this.selectedView || !this.selectedView.pages) {
			return 0;
		}

		let pages = this.selectedView.pages;
		let pageNumberIndex = this.selectedView.pages.findIndex(page => page.pageNumber === this.viewPageNumber);

		return this.isMainViewWindow ? pages[0].rows : pages[pageNumberIndex].rows;
	}

	public get selectedTileType() {
		return this.getSelectedTileType || {};
	}

	public set selectedTileType(tileType: any) {
		this.setSelectedTileType(tileType);
	}

	public async onFinishAdd(tileType: number) {
		this.unsetTileType();
	}

	public unsetTileType() {
		this.selectedTileType = this.noTileTypeId; 
	}


	public async initialiseEventBus() {
		// INITIALISE event bus.
		if (this.isMultiWindowViewEnabled) {
			if (!this.isMainViewWindow) {
				this.eventBus.$on("closeSubWindows", this.closePage);

				if (this.isPageKeeperEnabled) {
					this.eventBus.$on("subPageKeeperRequestFromMainPage", this.subPageKeeperRequestFromMainPage);
					this.eventBus.$on("subPageKeeperResponseFromMainPage", this.subPageKeeperResponseFromMainPage);
					this.eventBus.$emit("pageKeeperRemovePageExemption", this.viewPageNumber);
				}

					this.eventBus.$on("subPageEditModeSync", this.subPageEditModeSync);
			}
			else {
				if (this.isPageKeeperEnabled) {
					this.eventBus.$on("mainPageKeeperRequestFromSubPage", this.mainPageKeeperRequestFromSubPage);
					this.eventBus.$on("mainPageKeeperResponseFromSubPage", this.mainPageKeeperResponseFromSubPage);
					this.eventBus.$on("pageKeeperRemovePageExemption", this.pageKeeperRemovePageExemption);
				}

				this.eventBus.$on("deletePageBroadcast", this.deletePage);

				this.eventBus.$on("mainPageCheckEditMode", this.mainPageCheckEditMode);
			}
		}

		this.eventBus.$on("editViewBroadcast", this.editView);
		this.eventBus.$on("cancelViewEditBroadcast", this.cancelViewEdit);
		this.eventBus.$on("endEditingBroadcast", this.endEditing);

		this.eventBus.$on("setTileType", this.setTileType);
	}

	public async mounted() {
		// TODO: come back to this to implement propperly
		window['viewEventBus'] = this.eventBus;

		this.eventBus.$on("finishedAdd", this.onFinishAdd);

		// User groups
		this.userGroups = await api.getUserGroup();	
		
		await this.getValueFromPrefs();
		this.SetMaxWidthAndHeightFromPref();

		if (this.isFullUser) {
			// load the views the user would like to have on screen
			await this.loadViews();

			const viewsWithUserGroupsOnly: boolean = this.mobile;
			await this.loadUserViews(viewsWithUserGroupsOnly);
		} else {
			// the user is an adhoc user, get the view for this adhoc session
			await this.loadAdhocUserView(this.$route.query.sessionId);
		}

		// when user recieves a share view email, they will be given a link to the view by specific id
		if (this.$route.query.id) {
			await this.changeSelectedView(this.$route.query.id);
			return;
		}

		// set the current view to to be the first in the list
		if (this.currentUserViews.length > 0) {
			if (!this.isMainViewWindow) {
				let view = this.userViews.find(view => view.viewId === this.viewIDOverride);
				if (view) {
					this.setSelectedViewPageNumber(this.viewPageNumber);
					await this.changeSelectedView(this.viewIDOverride);
				}
			}
			else {
				await this.changeSelectedView(this.currentUserViews[0].viewId);
			}
		}

		await this.initialiseEventBus();

		window.addEventListener("resize", this.onWindowResized);

		if (!this.isMainViewWindow) {
			this.eventBus.$emit("mainPageCheckEditMode", this.selectedView);
		}
	}

	private SetMaxWidthAndHeightFromPref() {
		const myArr = this.defaultViewGridSize.toLowerCase().split("x");			
		this.maxWidth = this.parseStringValueToInt(myArr[0]);
		this.maxHeight = this.parseStringValueToInt(myArr[1]);
	}

	private async getValueFromPrefs() : Promise<void> {
		await this.getPrefsFromList([this.defaultGridSizePrefRequest]);
	}

	private parseStringValueToInt(x: string): number {
		const parsed = parseInt(x);
		if (isNaN(parsed)) { 
			return 5; 
		}
		if(parsed <= 0) {
			return 1;
		}
		return parsed;
	}	

	public get selectedUserGroups() {
		return this.viewUserGroups;
	}

	public set selectedUserGroups(value) {			
		this.viewUserGroups = value;
		this.viewUserGroupAdded = true;

		if (this.viewUserGroups.length > 0)
		{
			this.setSelectedViewPublic(true);
			this.setSelectedViewHasUserGroups(true);
		}
	}

	public deselectViewUserGroup(option) {

		if (this.viewUserGroupAdded) {
			this.viewUserGroupAdded = false;
			return;
		}

		const viewUserGroupFound = this.viewUserGroups.some(userGroup => userGroup.userGroupId === option.userGroupId);
		if (viewUserGroupFound) {
			const viewUserGroupIds = this.viewUserGroups.map(userGroup => userGroup.userGroupId);	
			const index = viewUserGroupIds.indexOf(option.userGroupId);
			if (index > -1) {
				this.viewUserGroups.splice(index, 1);
			}
		}
	}

	public destroyed() {
		if (!this.isMainViewWindow) {
			this.eventBus.$off("closeSubWindows", this.closePage);
			this.eventBus.$off("subPageEditModeSync", this.subPageEditModeSync);
		}
		else {
			this.eventBus.$emit("closeSubWindows", 0);
			this.eventBus.$off("deletePageBroadcast", this.deletePageBroadcast);
			this.eventBus.$off("mainPageCheckEditMode", this.mainPageCheckEditMode);
		}

		this.eventBus.$off("editViewBroadcast", this.editView);
		this.eventBus.$off("cancelViewEditBroadcast", this.cancelViewEdit);
		this.eventBus.$off("endEditingBroadcast", this.endEditing);

		this.destroyPageKeeper();

		window.removeEventListener("resize", this.onWindowResized);
		this.eventBus.$off("finishedAdd", this.onFinishAdd);
		this.eventBus.$off("setTileType", this.setTileType);
	}

	public onWindowResized() {
		this.$refs.viewsScrollbar?.update();
	}

	public beforeDestroy() {
		clearInterval(this.checkChangesInterval);
	}

	@Watch("$route.query.id")
	async onRouteChange(newViewId, oldViewId) {
		await this.changeSelectedView(newViewId);
	}

	public cellBackground(x: number, y: number) {
		if (y <= this.resizeHoverHeight && x <= this.resizeHoverWidth) {
			if (y <= this.height && x <= this.width) {
				return {
					backgroundColor: "#9ccbf0"
				};
			} else {
				return {
					backgroundColor: "#aaa"
				};
			}
		} else if (y <= this.height && x <= this.width) {
			return {
				backgroundColor: "#2196F3"
			};
		} else {
			return {};
		}
	}

	public cellHover(x: number, y: number) {
		this.resizeHoverWidth = x;
		this.resizeHoverHeight = y;
	}

	public resizeView(x: number, y: number): void {
		this.setViewDimensions({ x: x, y: y, pageNumber: this.isMainViewWindow ? 1 : this.viewPageNumber});
	}

	public async changeSelectedView(viewId, preventOpeningOfPages: boolean = false, preventClosingOfPages: boolean = false) {
		if (!preventClosingOfPages && this.isMainViewWindow	&& this.isMultiWindowViewEnabled) {
			this.eventBus.$emit("closeSubWindows", 0);
		}

		if (this.editing) {
			await this.cancelViewEditBroadcast();
		}

		// Clear our update poll interval
		clearInterval(this.checkChangesInterval);

		// set ViewMode
		await this.setViewMode(ViewMode.None);

		// Get View details such as cells, height, width
		await this.getViewDetails(viewId);

		// Reset the last time our content was updated
		this.contentLastUpdated = Date.now();

		// Setup our interval for checking layout/content changes
		this.checkChangesInterval = setInterval(() => {
			this.checkLayoutUpdated();
			this.checkContentUpdated();
		}, 5000);

		if (this.isMainViewWindow && !preventOpeningOfPages && this.isMultiWindowViewEnabled) {
			this.openAllPages();
		}

		if(this.isPageKeeperEnabled) {
			this.reinitialisePageKeeper();
		}
	}

	/**
	 * Method to invoke the correct pageKeeper methods according to 
	 * whether or not they are the main or sub page.
	 * The objective here is to ensure all windows are open/closed appropriately.
	 */
	public initiatePageKeeper() {
		this.pageKeeperExpectedResponses = this.getPageKeeperResponsesTemplate(1);
		this.pageKeeperResponses = this.getPageKeeperResponsesTemplate(0);
		this.requestHasBeenRespondedTo = true;

		if (this.isMainViewWindow && !this.pageKeeperInterval) {
			this.pageKeeperInterval = setInterval(() => {
				this.pageKeeperMainWindowIntervalJob();
			}, 3000);
		}
		else {
			this.pageKeeperInterval = setInterval(() => {
				this.pageKeeperSubWindowIntervalJob();
			}, 3000);
		}
	}

	/**
	 * The actions that should occur on an interval for the main window page keeper
	 */
	public async pageKeeperMainWindowIntervalJob() {
		// Determine if we have been responded to appropriately.
		if (this.pageKeeperResponses.every(pageResponse => {
			let expectedResponseCount = this.pageKeeperExpectedResponses.find(pageExpectedResponse => {
				return pageExpectedResponse.pageNumber === pageResponse.pageNumber;
			}).responseCount;

			return pageResponse.responseCount >= expectedResponseCount;
		})) {
			this.requestHasBeenRespondedTo = true;
		}
		else {
			let unresponsivePages = this.getUnresponsivePageWindows();
			this.requestHasBeenRespondedTo = unresponsivePages.every(page => {
				return this.pagesExemptFromPageKeeperChecks.includes(page.pageNumber);
			});
		}

		// Check for presence of sub windows.
		if (this.requestHasBeenRespondedTo) {
			this.mainPageKeeperSendBroadcast();
		}
		else {
			let unresponsivePages = this.getUnresponsivePageWindows();

			unresponsivePages.forEach(pageResponse => {
				if (!this.pagesExemptFromPageKeeperChecks.includes(pageResponse.pageNumber)) {
					this.openPage(pageResponse.pageNumber);
					setTimeout(() => {
						this.pageKeeperRemovePageExemption(pageResponse.pageNumber);
					}, 110000); // Forcefully remove exemption after 2 minutes (110 seconds, 10 seconds inside method).
				}
			});

			this.requestHasBeenRespondedTo = true;
		}

		// Ensure sub windows are in edit mode if main window is in edit mode
		// sub window can occasionally NOT be in edit mode if the sub window was opened after edit mode was enabled
		// This can occur if the user closes the window and pagekeeper re-opens it.
		if (this.editing) {
			this.eventBus.$emit("subPageEditModeSync", this.selectedView);
		}
	}

	/**
	 * Remove a page from the exempt list (exempt from being checked/opened if unresponsive 
	 * (give enough time for page to open and mount))
	 */ 
	public async pageKeeperRemovePageExemption(pageNumberToRemove: number) {
		setTimeout(() => {
			this.pagesExemptFromPageKeeperChecks.removeWhere(pageNumber => pageNumber === pageNumberToRemove);
		}, 10000); // Forcefully remove exemption after 10 seconds.
	}

	/**
	 * Gets a list of unresponsive page windows (windows that have less responses than expected)
	 */
	public getUnresponsivePageWindows(): PageKeeperResponse[] {
		return this.pageKeeperResponses.filter(pageResponse => {
			return !this.pageKeeperExpectedResponses.some(expectedPageResponse => {
				return (
					pageResponse.pageNumber === expectedPageResponse.pageNumber &&
					pageResponse.responseCount >= expectedPageResponse.responseCount
				);
			})
		});
	}

	/**
	 * The actions that should occur on an interval for the sub window page keeper
	 */
	public async pageKeeperSubWindowIntervalJob() {
		if (this.requestHasBeenRespondedTo) {
			this.subPageKeeperSendBroadcast();
		}
		else {
			this.closePage();
		}
	}

	/**
	 * Turns off the PageKeeper eventbus broadcasts and clears the relevantinterval
	 */
	public destroyPageKeeper(destroyEventBusBroadcasts: boolean = true) {
		if (destroyEventBusBroadcasts) {
			if (this.isMainViewWindow) {
				this.eventBus.$off("mainPageKeeperRequestFromSubPage", this.mainPageKeeperRequestFromSubPage);
				this.eventBus.$off("mainPageKeeperResponseFromSubPage", this.mainPageKeeperResponseFromSubPage);
				this.eventBus.$off("pageKeeperRemovePageExemption", this.pageKeeperRemovePageExemption);
			} 
			else {
				this.eventBus.$off("subPageKeeperRequestFromMainPage", this.subPageKeeperRequestFromMainPage);
				this.eventBus.$off("subPageKeeperResponseFromMainPage", this.subPageKeeperResponseFromMainPage);
				this.eventBus.$off("subPageEditModeSync", this.subPageEditModeSync);
			}
		}

		this.pagesExemptFromPageKeeperChecks = [];
		
		clearInterval(this.pageKeeperInterval);
	}

	/**
	 * Reinitialises the page keeper by destroying it and recreating it.
	 */
	public reinitialisePageKeeper() {
		this.destroyPageKeeper(false);
		setTimeout(() => {
			this.initiatePageKeeper();
		}, 5000);
	}

	/**
	 * Resets the array that keeps track of the Page Keeper responses accordingly.
	 */
	public getPageKeeperResponsesTemplate(initialResponseCount: number): PageKeeperResponse[] {
		let pageKeeperResponses: PageKeeperResponse[] = [];

		if (this.isMainViewWindow) {
			this.selectedView.pages?.forEach(page => {
				if (page.pageNumber !== 1) {
					let subPageResponse = {pageNumber: page.pageNumber, responseCount: initialResponseCount};
					pageKeeperResponses.push(subPageResponse);
				}
			});
		}
		else {
			let mainPageResponse = {pageNumber: 1, responseCount: initialResponseCount};
			pageKeeperResponses = [mainPageResponse];
		}

		return pageKeeperResponses;
	}

	/**
	 * Broadcast a message from the main page keeper to all sub windows
	 */
	public async mainPageKeeperSendBroadcast() {
		this.pageKeeperResponses = this.getPageKeeperResponsesTemplate(0);
		this.requestHasBeenRespondedTo = false;
		this.eventBus.$emit("subPageKeeperRequestFromMainPage", null);
	}

	/**
	 * The handler invoked whenever a response is received from the sub pages responding to the main pagekeeper.
	 */
	public async mainPageKeeperResponseFromSubPage(subPageNumber: number) {
		let response = this.pageKeeperResponses.find(pageKeeperResponse => pageKeeperResponse.pageNumber == subPageNumber);
		response.responseCount++;
	}

	/**
	 * The handler invoked whenever the main page keeper receives a request from a sub window checking for presence.
	 */
	public async mainPageKeeperRequestFromSubPage(subPageNumber: number) {
		this.eventBus.$emit("subPageKeeperResponseFromMainPage", subPageNumber);
	}

	/**
	 * Broadcast a message fromt he sub page keeper to the main window
	 */
	public async subPageKeeperSendBroadcast() {
		this.pageKeeperResponses = this.getPageKeeperResponsesTemplate(0);
		this.requestHasBeenRespondedTo = false;
		this.eventBus.$emit("mainPageKeeperRequestFromSubPage", this.viewPageNumber);
	}

	/**
	 * The handler invoked whenever a response is received from the main page responding to the sub pagekeeper
	 */
	public async subPageKeeperResponseFromMainPage(requestingPage: number) {
		if (requestingPage === this.viewPageNumber) {
			let response = this.pageKeeperResponses.find(pageKeeperResponse => pageKeeperResponse.pageNumber == 1);
			response.responseCount++;
			this.requestHasBeenRespondedTo = true;
		}
	}

	/**
	 * The handler invoked whenever the sub page keeper receives a request from a main window checking for presence.
	 * A response should be sent.
	 */
	public async subPageKeeperRequestFromMainPage() {
		this.eventBus.$emit("mainPageKeeperResponseFromSubPage", this.viewPageNumber);
	}

	/**
	 * The handler invoked whenever the master page keeper sends a message to sub windows toggling edit mode if 
	 * the sub window is not in edit mode when it should be.
	 */
	public async subPageEditModeSync(view: ViewObject) {
		if (!this.editing) {
			this.editView(view);
		}
	}

	/**
	 * 
	 */
	public async mainPageCheckEditMode(view: ViewObject) {
		if (this.editing) {
			this.eventBus.$emit("subPageEditModeSync", view);
		}
	}

	public async openAllPages() {
		this.pagesOpenedRecently = [];
		if (this.selectedView?.pages.length > 0) {
			this.selectedView.pages.forEach((page) => {
				if (page.pageNumber > 1) {
					this.openPage(page.pageNumber);
				}
			});
		}
	}

	public openPage(pageNumber: number) {
		if (this.isPageKeeperEnabled) {
			this.pagesExemptFromPageKeeperChecks.push(pageNumber);
			setTimeout(() => {
				this.pageKeeperRemovePageExemption(pageNumber);
			}, 110000); // Forcefully remove exemption after 2 minutes (110 seconds, 10 seconds inside method).
		}

		if (!this.pagesOpenedRecently.includes(pageNumber)) {
			const routeData = router.resolve({
				path: `/views/${this.selectedView.viewId}/page/${pageNumber}?guid=${this.viewGUID}`
			});
			window.open(routeData.href, "_blank", "location,scrollbars");

			this.pagesOpenedRecently.push(pageNumber);

			setTimeout(() => {
				this.removePageFromRecentlyAddedList(pageNumber);
			}, 10000);
		}
	}

	public async removePageFromRecentlyAddedList(pageNumberToRemove: number) {
		this.pagesOpenedRecently.removeWhere(pageNumber => pageNumber === pageNumberToRemove);
	}

	public async addNewPage() {
		let newPageNumber = 0;
		// Find the next available pagenumber
		this.selectedView.pages.forEach(page => {
			// if unset
			if (newPageNumber === 0 ) {
				let nextPageNumber = page.pageNumber + 1;
				// if we dont have a page that contains the current pagenumber + 1 
				if (!this.selectedView.pages.some(pageCheck => {
					return pageCheck.pageNumber === nextPageNumber;
				})) {
					newPageNumber = nextPageNumber;
				}
			}
		});
		let currentViewId = this.selectedView.viewId;
		let newPageCells: ViewCellObject[] = []; 
		let newPage: ViewPage = {
			columns: this.maxWidth, 
			rows: this.maxHeight, 
			pageNumber: newPageNumber, 
			viewId: currentViewId,
			cells: newPageCells,
		};
		
		let pages = [...this.selectedView.pages, newPage];

		this.updateViewPages(pages);
		this.updateSelectedViewPages(pages);
	
		await this.saveView(true);

		this.pageKeeperExpectedResponses = this.getPageKeeperResponsesTemplate(1);

		this.openPage(newPageNumber);
	}

	public closePage(specifiedPage: number = 0) {
		if (!this.isMainViewWindow) {
			if (specifiedPage === 0 || specifiedPage === this.viewPageNumber) {
				this.eventBus.$emit("pageKeeperRemovePageExemption", this.viewPageNumber);
				window.close();
			}
		}
	}

	public async checkLayoutUpdated() {
		if (this.selectedView == null || this.selectedView.viewId == null) return;
		const viewUpdatedLast = await this.loadViewUpdateTime(this.selectedView.viewId);
		const lastUpdatedTime = Date.parse(viewUpdatedLast);
		const viewLoadedTime = Date.parse(this.selectedView.dateLoaded);

		if (lastUpdatedTime > viewLoadedTime) {
			if (this.editing) {
				if (!this.layoutExpired && this.isMainViewWindow) {
					const timeDiff = -(lastUpdatedTime - new Date().getTime()) / 1000;
					this.$notify({
						group: "expired",
						duration: -1,
						data: {
							timeDiff
						}
					});
				}
			} else {
				await this.refreshLayout();
			}

			this.layoutExpired = true;
		} else {
			this.layoutExpired = false;
		}
	}

	public async checkContentUpdated() {
		const viewContentUpdatedLast = await this.loadViewContentUpdateTime(this.selectedView.viewId);
		const lastUpdatedTime = Date.parse(viewContentUpdatedLast);

		if (lastUpdatedTime > this.contentLastUpdated) {
			await this.loadUpdatedCellContent(this.selectedView.viewId);
			this.contentLastUpdated = Date.now();
		}
	}

	public async editViewBroadcast(view: ViewObject) {
		this.eventBus.$emit("editViewBroadcast", view);
	}

	public async editView(view: ViewObject) {
		this.getViewDetails(view.viewId);
		await this.setViewMode(ViewMode.Edit);
		await this.setSelectedView(view);

		// View user groups
		const viewUserGroupIds = (await viewsApi.getUserGroupsFromView(view.viewId)).userGroupIds;
		this.viewUserGroups = this.userGroups.filter(userGroup => viewUserGroupIds.some(userGroupId => userGroupId === userGroup.userGroupId));

		this.$refs.viewTitleInput?.focus();
	}

	public async cancelViewEditBroadcast(resumingFromCreation: boolean = false) {
		this.eventBus.$emit("cancelViewEditBroadcast", resumingFromCreation);
	}

	public async cancelViewEdit(resumingFromCreation: boolean = false) {

		if (resumingFromCreation) {
			if (this.isMainViewWindow) {
				this.openAllPages();
			}
		}

		await this.refreshViewsLists();
		await this.setViewMode(ViewMode.None);

		if (this.newView) {
			if (this.currentUserViews.length > 0) {
				await this.changeSelectedView(this.currentUserViews[0].viewId, true, true);
			} else {
				await this.setSelectedView(null);
			}
			this.newView = null;
		} else {
			try {
				await this.changeSelectedView(
					this.currentUserViews.find(view => view.viewId == this.selectedView.viewId).viewId,
					true,
					true
				);
			} catch {
				await this.changeSelectedView(this.currentUserViews[0].viewId, true, true);
			}
		}

		if (this.isMainViewWindow) {
			await this.endEditingBroadcast();
		}
	}

	public async endEditingBroadcast() {
		this.eventBus.$emit("endEditingBroadcast", null);
	}

	public async endEditing() {
		await this.setViewMode(ViewMode.None);
		this.selectedTileType = null;
		this.$v.$reset();
		this.titleError = false;
	}

	public async createView(): Promise<void> {

		if (this.isMainViewWindow) {
			this.eventBus.$emit("closeSubWindows", 0);
		}

		this.newView = {
			viewId: null,
			title: "",
			public: false,
			cells: [],
			groupId: this.getUserGroupId,
			ownerUserId: this.currentUserId,
			locked: false,
			pages: [{ columns: this.maxWidth,  rows: this.maxHeight, pageNumber: 1, cells: [], viewId: 0 }],
			hasUserGroups: false
		};

		clearInterval(this.checkChangesInterval);
		await this.setSelectedView(this.newView);
		await this.setViewMode(ViewMode.Edit);

		// Set our title to be focused
		this.$refs.viewTitleInput.focus();

		this.viewUserGroups = [];
	}

	private openShareModal(view: any) {
		this.viewToShare = view;
		this.isShareModalOpen = true;
	}

	public async saveView(creatingNewPage: boolean = false) {
		try {
			clearInterval(this.checkChangesInterval);
			this.isAdding = true;
			this.$v.$touch();
			if (this.$v.title.$error) {
				this.errorMessage = "You must set a title";
				return;
			}

			if(this.selectedView.cells == null) {
				this.selectedView.cells = this.newView.pages[0].cells;
			}

			// View user groups 
			const viewUserGroupIds = this.viewUserGroups.map(userGroup => userGroup.userGroupId);
			const viewUserGroups = { UserGroupIds : viewUserGroupIds };
			this.setSelectedViewHasUserGroups(viewUserGroups?.UserGroupIds.length > 0);

			const savedView = await this.save(this.selectedView);
			if (savedView.viewId && !savedView.error) {

				await viewsApi.setUserGroupsToView(savedView.viewId, viewUserGroups);

				if (!creatingNewPage) {
					await this.endEditingBroadcast();
					await this.refreshViewsLists();
					await this.changeSelectedView(savedView.viewId, true, true);
				}
				this.titleError = false;

				if (this.newView) {
					this.newView = null;
				}
			} else {
				this.titleError = true;
				this.errorMessage = savedView.error;
			}
		} finally {
			this.isAdding = false;
		}

	}

	public async handleCurrentView(preventOpeningOfPages: boolean = false) {
		// after removing a view set the selected view to be the first available, if any
		if (this.currentUserViews.length > 0) {
			await this.changeSelectedView(this.currentUserViews[0].viewId, preventOpeningOfPages);
		} else {
			await this.setViewMode(ViewMode.None);
			await this.setSelectedView(null);
			clearInterval(this.checkChangesInterval);
		}
	}

	public async deleteView() {
		this.isDeleteConfirmationShown = false;
		await this.delete(this.selectedView.viewId);
		await this.refreshViewsLists();
		await this.handleCurrentView();
		await this.endEditingBroadcast();
	}

	public async deletePageBroadcast() {
		this.eventBus.$emit("deletePageBroadcast", this.viewPageNumber);
	}

	public async deletePage(pageToDelete: number) {
		this.isDeletePageConfirmationShown = false;

		let cellsWithoutThisPage = this.selectedView.cells.filter(c => c.pageNumber !== this.viewPageNumber);
		
		this.setSelectedViewCells(cellsWithoutThisPage);
		
		let pages = this.selectedView.pages.filter(page => page.pageNumber !== pageToDelete);

		this.deletePageCells(pageToDelete);

		this.updateViewPages(pages);
		this.updateSelectedViewPages(pages);
	
		await this.saveView(true);

		this.eventBus.$emit("closeSubWindows", pageToDelete);
	}

	public async addView(viewId) {
		await this.add(viewId);
		await this.refreshViewsLists();
		await this.setViewMode(ViewMode.None);
		await this.changeSelectedView(viewId);
	}


	public async cloneView(viewId): Promise<void> {
		this.originalView = await this.getViewProperties(viewId);

		const copiedCells: ViewCellObject[] = [];
		const allCellsFromOriginalView: ViewCellDTO[] = this.originalView.pages.reduce<ViewCellDTO[]>(
			(accumulator, currentValue) => accumulator.concat(currentValue.cells),
			[]
		);

		allCellsFromOriginalView.forEach((cell: ViewCellDTO) => {
			copiedCells.push({
				viewCellId: -Math.floor(100000 + Math.random() * 900000),
				width: cell.columnSpan,
				height: cell.rowSpan,
				pluginTypeId: cell.pluginId,
				x: cell.x,
				y: cell.y,
				contents: cell.contents,
				pageNumber: cell.pageNumber
			});
		});

		// View user groups
		const viewUserGroupIds = (await viewsApi.getUserGroupsFromView(viewId)).userGroupIds;
		this.viewUserGroups = this.userGroups.filter(userGroup => viewUserGroupIds?.some(userGroupId => userGroupId === userGroup.userGroupId));
		this.newView = {
			viewId: null,
			title: null,
			public: this.viewUserGroups && this.viewUserGroups.length > 0 ? true : false,
			groupId: this.getUserGroupId,
			ownerUserId: this.currentUserId,
			cells: copiedCells,
			pages: this.originalView.pages as any[],
			locked: false,
			hasUserGroups: this.viewUserGroups && this.viewUserGroups.length > 0 ? true : false
		};


		clearInterval(this.checkChangesInterval);
		
		await this.setSelectedView(this.newView);
		await this.setViewMode(ViewMode.Edit);

		// Set our title to be focused
		this.$refs.viewTitleInput.focus();
	}

	public async lockClicked(view){
		this.viewToLock = view;
		this.modalShow = true;
	}

	public async lockView(){
		await this.toggleLock(this.viewToLock.viewId);
		await this.refreshViewsLists();
		await this.changeSelectedView(this.selectedView.viewId);
		this.modalShow = false;
	}

	public async hideLockModal(){
		this.modalShow = false;
	}

	public async removeView(viewId) {
		await this.remove(viewId);
		await this.refreshViewsLists();
		await this.handleCurrentView();
	}

	public async refreshViewsLists() {
		await this.loadViews();

		const viewsWithUserGroupsOnly: boolean = this.mobile;
		await this.loadUserViews(viewsWithUserGroupsOnly);
	}

	public async refreshLayout() {
		await this.changeSelectedView(this.selectedView.viewId);
	}

	public setTileTypeBroadcast(tileType: any) {
		this.eventBus.$emit("setTileType", tileType);
	}

	public setTileType(tileType: any) {
		this.showMaxPluginError = false;
		if (this.selectedTileType.id === tileType.id) {
			this.selectedTileType = null;
		} 
		else if (
			!tileType.maxInstances ||
			tileType.maxInstances > this.selectedView.cells.filter(cell => cell.pluginTypeId == tileType.id).length
		) {
			this.selectedTileType = tileType;
		} 
		else {
			this.showMaxPluginError = true;
		}
	}

	private async onSelectedViewChanged (view: ViewObject) {	
		if (view.viewId != this.selectedView.viewId) {
			await this.changeSelectedView(view.viewId) 
		}
	}
}
